import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/company-profile.js
import { authService } from '@/services/auth';
import { permissionsService } from '@/services/permissions/permissions';

/**
 * CompanyProfileHandler
 * 
 * Manages company profile data with features for:
 * - Loading and caching profile information with freshness checks
 * - Parsing and standardizing Snowflake data format
 * - Profile updates with optimistic UI updates
 * - Admin user management and main user transfer
 * - Request deduplication to prevent duplicate network calls
 */
export class CompanyProfileHandler {
  /**
   * Initialize the company profile handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    this.CACHE_FRESHNESS_THRESHOLD = 30 * 60 * 1000;
    this.lastProfileFetch = null;
    this.lastAdminUsersFetch = null;
    this.isLoading = false;
    this.store.state.data.companyProfile = null;
    this.store.state.data.parsedProfile = null;
    this.pendingRequests = {};
  }

  /**
   * Initialize the handler - called during app store initialization
   * NOTE: Actual data loading now happens in appStore._loadIdentityData()
   * This method only handles existing cached data parsing
   * @returns {Promise<boolean>} Success status
   */
  async initialize() {
    try {
      // Parse existing data if available (from cache restore)
      if (this.store.state.data.companyProfile) {
        if (!this.store.state.data.parsedProfile) {
          this.store.state.data.parsedProfile = this.parseSnowflakeData(
            this.store.state.data.companyProfile
          );
        }
      }

      // ✅ REMOVED: Fire-and-forget loadProfileData call
      // Profile loading now happens in appStore._loadIdentityData() which
      // properly awaits the data before any routing decisions are made.
      // This fixes the race condition where heavy accounts would have
      // incorrect firstRun/privilege status.

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[CompanyProfileHandler] Initialization error:', error);
      }
      return false;
    }
  }

  /**
   * Parse Snowflake data consistently to handle different case formats
   * @param {Object} data - Snowflake data object
   * @returns {Object|null} Parsed data with company and personal objects
   */
  parseSnowflakeData(data) {
    if (!data) return null;

    let companyData = data.CompanyData || data.COMPANYDATA;
    let personalData = data.PersonalData || data.PERSONALDATA;

    if (typeof companyData === 'string') {
      try {
        companyData = JSON.parse(companyData);
      } catch (e) {
        // Silent error in production
      }
    }

    if (typeof personalData === 'string') {
      try {
        personalData = JSON.parse(personalData);
      } catch (e) {
        // Silent error in production
      }
    }

    companyData = companyData || {};
    personalData = personalData || {};
    companyData.Email = personalData.Email || "";

    return { company: companyData, personal: personalData };
  }

  /**
   * Load profile data with caching and improved error handling
   * @param {boolean} forceRefresh - Force refresh regardless of cache
   * @returns {Promise<Object|null>} Profile data or null
   */
  async loadProfileData(forceRefresh = false) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        return null;
      }

      const requestKey = `loadProfile-${projectId}`;
      if (this.pendingRequests[requestKey]) {
        return this.pendingRequests[requestKey];
      }

      if (!forceRefresh && this.lastProfileFetch &&
        Date.now() - this.lastProfileFetch < this.CACHE_FRESHNESS_THRESHOLD &&
        this.store.state.data.companyProfile) {
        return this.store.state.data.companyProfile;
      }

      this.isLoading = true;
      this.store.state.loading.companyProfile = true;

      const loadPromise = (async () => {
        try {
          const authToken = authService.getAuthState()?.token;
          const isPageReload = typeof window !== 'undefined' &&
            window.performance?.navigation?.type === 1;

          if (isPageReload && !authToken && this.store.state.data.companyProfile) {
            return this.store.state.data.companyProfile;
          }

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);

          try {
            const response = await fetch(
              `${process.env.GATSBY_STRAPI_URL}/api/company-profile/${projectId}`,
              {
                headers: {
                  'Authorization': `Bearer ${getStrapiToken() || authToken}`
                },
                signal: controller.signal
              }
            );

            clearTimeout(timeoutId);

            if (!response.ok) {
              if (isPageReload && this.store.state.data.companyProfile) {
                return this.store.state.data.companyProfile;
              }
              throw new Error(`Failed to load profile: ${response.status}`);
            }

            const data = await response.json();
            const profileData = data.data;
            const parsedData = this.parseSnowflakeData(profileData);

            this.store.state.data.companyProfile = profileData;
            this.store.state.data.parsedProfile = parsedData;
            this.lastProfileFetch = Date.now();

            return profileData;
          } catch (fetchError) {
            clearTimeout(timeoutId);

            if (isPageReload &&
              (fetchError instanceof TypeError || fetchError.name === 'AbortError') &&
              this.store.state.data.companyProfile) {
              return this.store.state.data.companyProfile;
            }

            throw fetchError;
          }
        } finally {
          this.isLoading = false;
          this.store.state.loading.companyProfile = false;
          delete this.pendingRequests[requestKey];
          this.store.notifySubscribers('companyProfile');
        }
      })();

      this.pendingRequests[requestKey] = loadPromise;
      return loadPromise;
    } catch (error) {
      this.store.handleError('companyProfile', error);
      this.isLoading = false;
      this.store.state.loading.companyProfile = false;
      return null;
    }
  }

  /**
   * Get parsed profile data from store or parse raw data if needed
   * @returns {Object|null} Parsed profile data or null if not available
   */
  getParsedProfileData() {
    if (this.store.state.data.parsedProfile) {
      return this.store.state.data.parsedProfile;
    }

    const rawData = this.store.state.data.companyProfile;
    if (!rawData) return null;

    const parsedData = this.parseSnowflakeData(rawData);
    this.store.state.data.parsedProfile = parsedData;
    return parsedData;
  }

  /**
   * Update company profile with optimistic UI updates
   * @param {Object} companyData - Company data object to update
   * @param {Object} personalData - Personal data object to update
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If user doesn't have permission to edit profile
   */
  async updateProfileData(companyData, personalData) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        return false;
      }

      if (!permissionsService.canEditProfile()) {
        throw new Error('You do not have permission to edit the company profile');
      }

      this.store.state.loading.companyProfile = true;
      const oldData = this.store.state.data.companyProfile;

      // Optimistic update - update local state first
      if (oldData) {
        this.store.state.data.companyProfile = {
          ...oldData,
          CompanyData: companyData,
          PersonalData: personalData
        };

        this.store.state.data.parsedProfile = {
          company: { ...companyData, Email: personalData.Email },
          personal: personalData
        };

        this.store.notifySubscribers('companyProfile');
      }

      const authToken = authService.getAuthState()?.token;
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/company-profile/${projectId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken() || authToken}`
          },
          body: JSON.stringify({
            companyData,
            personalData
          })
        }
      );

      if (!response.ok) {
        // Revert optimistic update if request fails
        if (oldData) {
          this.store.state.data.companyProfile = oldData;
          this.store.state.data.parsedProfile = this.parseSnowflakeData(oldData);
          this.store.notifySubscribers('companyProfile');
        }
        throw new Error(`Failed to update profile: ${response.status}`);
      }

      // Update cache timestamp
      this.lastProfileFetch = Date.now();
      return true;
    } catch (error) {
      this.store.handleError('companyProfile', error);
      return false;
    } finally {
      this.store.state.loading.companyProfile = false;
    }
  }

  /**
   * Load admin users for main user transfer functionality with caching
   * @param {boolean} forceRefresh - Force refresh regardless of cache
   * @returns {Promise<Array>} List of admin users (excluding current user)
   */
  async loadAdminUsers(forceRefresh = false) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        return [];
      }

      const requestKey = `loadAdminUsers-${projectId}`;
      if (this.pendingRequests[requestKey]) {
        return this.pendingRequests[requestKey];
      }

      // Use cache if available and fresh
      if (!forceRefresh && this.lastAdminUsersFetch &&
        Date.now() - this.lastAdminUsersFetch < this.CACHE_FRESHNESS_THRESHOLD) {
        const cachedUsers = this.store.state.data.users?.list;
        if (cachedUsers?.length > 0) {
          const adminUsers = this.filterAdminUsers(cachedUsers);
          if (adminUsers.length > 0) {
            return adminUsers;
          }
        }
      }

      const loadPromise = (async () => {
        const isPageReload = typeof window !== 'undefined' &&
          window.performance?.navigation?.type === 1;

        try {
          // Try to get users from usersHandler if available
          if (this.store.usersHandler && typeof this.store.usersHandler.loadUsers === 'function') {
            const allUsers = await this.store.usersHandler.loadUsers(forceRefresh);
            if (allUsers?.length > 0) {
              const adminUsers = this.filterAdminUsers(allUsers);
              this.lastAdminUsersFetch = Date.now();
              return adminUsers;
            }
          }

          // Fallback to direct API call
          const response = await fetch(
            `${process.env.GATSBY_STRAPI_URL}/api/user-management/project/${projectId}/users`,
            {
              headers: {
                'Authorization': `Bearer ${getStrapiToken() || authService.getAuthState()?.token}`
              }
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to load users: ${response.status}`);
          }

          const data = await response.json();
          const usersList = data.data || [];
          const adminUsers = this.filterAdminUsers(usersList);
          this.lastAdminUsersFetch = Date.now();
          return adminUsers;
        } catch (error) {
          if (isPageReload) {
            return [];
          }
          throw error;
        } finally {
          delete this.pendingRequests[requestKey];
        }
      })();

      this.pendingRequests[requestKey] = loadPromise;
      return loadPromise;
    } catch (error) {
      return [];
    }
  }

  /**
   * Filter admin users from user list, excluding current user
   * @private
   * @param {Array} users - List of users
   * @returns {Array} List of admin users excluding current user
   */
  filterAdminUsers(users) {
    const currentUserEmail = authService.getAuthState()?.user?.email?.toLowerCase();
    return users.filter(user =>
      user.role === 'Admin' &&
      user.email?.toLowerCase() !== currentUserEmail
    );
  }

  /**
   * Transfer main user role to another admin
   * @param {string} newMainUserId - User ID of new main user
   * @param {boolean} adminOverride - Whether this is an admin overriding the permission check
   * @returns {Promise<Object>} Result with success status and message
   * @throws {Error} If user lacks permission or parameters are invalid
   */
  async transferMainUser(newMainUserId, adminOverride = false) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error("No project ID available");
      }

      if (!newMainUserId) {
        throw new Error("No user ID provided for transfer");
      }

      if (!permissionsService.isMainUser() && !adminOverride) {
        throw new Error("You don't have permission to transfer the main user role");
      }

      const authToken = authService.getAuthState()?.token;
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/user-management/transfer-main-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken() || authToken}`
          },
          body: JSON.stringify({
            projectId,
            newMainUserId
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to transfer main user (${response.status})`);
      }

      // Invalidate cache
      this.lastProfileFetch = null;
      this.lastAdminUsersFetch = null;

      // Refresh data
      await this.loadProfileData(true);

      return {
        success: true,
        message: "Main user has been transferred successfully."
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to transfer main user."
      };
    }
  }

  /**
   * Check if profile data is stale and needs refreshing
   * @returns {boolean} True if data is stale or doesn't exist
   */
  isProfileDataStale() {
    return !this.store.state.data.companyProfile ||
      !this.lastProfileFetch ||
      (Date.now() - this.lastProfileFetch > this.CACHE_FRESHNESS_THRESHOLD);
  }

  /**
   * Refresh profile data in background without blocking UI
   * @param {boolean} onlyIfStale - Only refresh if data is stale
   * @returns {Promise<boolean>} Success status
   */
  async refreshProfileInBackground(onlyIfStale = true) {
    if (this.isLoading) return false;

    if (onlyIfStale && !this.isProfileDataStale()) {
      return false;
    }

    try {
      const projectId = this.store.getProjectId();
      if (!projectId) return false;

      const originalIsLoading = this.isLoading;
      this.isLoading = false;
      await this.loadProfileData(true);
      this.isLoading = originalIsLoading;
      return true;
    } catch (error) {
      return false;
    }
  }
}